<template>
  <div class="clerk-bottom-bar">
    <ControlGroup label="Event" tone="danger">
      <Button
        severity="danger"
        size="small"
        icon="pi pi-stop-circle"
        label="Halt Event"
        @click="emit('halt')"
      />
      <span class="clerk-bottom-bar__divider" />
      <EventRunningTime :started-at-seconds="eventStartedAtSeconds" />
    </ControlGroup>

    <span class="clerk-bottom-bar__spacer" />

    <ControlGroup label="Item · run order">
      <Button
        severity="secondary"
        icon="pi pi-chevron-left"
        title="Previous run"
        :disabled="!canGoPrevious"
        :style="iconButtonFootprint"
        @click="emit('previous')"
      />
      <RunSearchField v-model="runNumber" @search="(value) => emit('run-search', value)" />
      <Button
        severity="secondary"
        icon="pi pi-chevron-right"
        title="Next run"
        :disabled="!canGoNext"
        :style="iconButtonFootprint"
        @click="emit('next')"
      />
    </ControlGroup>
  </div>
</template>

<script setup>
import Button from 'primevue/button';
import ControlGroup from '../ControlGroup/ControlGroup.vue';
import EventRunningTime from '../EventRunningTime/EventRunningTime.vue';
import RunSearchField from '../RunSearchField/RunSearchField.vue';

// The event bar between the action zone and the bottom band: stopping the sale
// on the left, moving the block on the right.
//
// Halt Event and the run arrows are plain PrimeVue `Button`s — a labeled
// danger button and two icon-only secondaries — themed under `components.button`
// in the preset. The `Halt Event` button takes `severity="danger"`, the arrows
// carry a native `title` for their tooltip and a fixed square footprint the bar
// lays out against.
//
// The two clusters are pushed to opposite ends deliberately. Halt Event and the
// run-order arrows are the two most consequential controls on the console and
// the two easiest to hit by accident, so there is a whole console's width
// between them.
//
// It resolved the adjacent runs itself and called `moveToItem`. It reports
// `previous` and `next` now and is told whether either exists.
defineOptions({ name: 'ClerkBottomBar' });

defineProps({
  // Seconds the sale had already been running, handed to the pace clock.
  eventStartedAtSeconds: { type: Number, default: 0 },
  canGoPrevious: { type: Boolean, default: false },
  canGoNext: { type: Boolean, default: false },
});

const emit = defineEmits(['halt', 'previous', 'next', 'run-search']);

// What is typed in the run box, so the application can clear it once a run is
// found or seed it from a scanner.
const runNumber = defineModel({ type: String, default: '' });

// The square footprint the run arrows lay out against. Structure, not theme —
// an icon-only Button is not otherwise square — so it stays an inline style
// rather than a token.
const iconButtonFootprint = { width: '34px', height: '34px' };
</script>

<style scoped>
.clerk-bottom-bar {
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  background: var(--p-clerk-bottom-bar-background);
  border: var(--p-clerk-bottom-bar-border-width) solid var(--p-clerk-bottom-bar-border-color);
  border-radius: var(--p-clerk-bottom-bar-border-radius);
  padding: var(--p-clerk-bottom-bar-padding);
}

.clerk-bottom-bar__spacer {
  flex: 1;
}

.clerk-bottom-bar__divider {
  width: 1px;
  height: 22px;
  background: var(--p-clerk-bottom-bar-divider-background);
}
</style>
